// cardapioSection/index.ts (estilos)
//
// Folha única da aba Cardápio: as cinco sub-telas (Visão geral, Produtos,
// Combos, Preço por canal, Saneamento) são a MESMA anatomia — cartão, lista
// nominal e tabela — e uma folha por sub-tela só repetiria o mesmo kit cinco
// vezes.
//
// Anatomia copiada de storesSection/MeiosPagamento (FieldsContainer raiz,
// Header/HeaderTitle/HeaderSubtitle, EmptyState, Aviso com $tom). Tamanhos só
// na escada de tokens; o que não cai nos valores de --sp- vira
// `calc(var(--u) * N)`. Cores só de `props.theme.colors` — que hoje tem
// primary, background, text, selection e modalColor; `borderColor` resolve
// undefined e por isso vai sempre com fallback (dívida herdada do repo, não
// consertada aqui).
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(calc(var(--u) * -10)); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-20);
  gap: var(--sp-16);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const HeaderContent = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 260);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-3xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

// ===== AVISO DE SOMENTE LEITURA (topo de todas as sub-telas) =====
export const AvisoLeitura = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-12);
  margin-bottom: var(--sp-20);
  padding: var(--sp-12) var(--sp-16);
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
  animation: ${fadeIn} 0.3s ease;
`;

export const AvisoLeituraTexto = styled.div`
  flex: 1;
`;

export const AvisoLeituraLinha = styled.p`
  margin: 0 0 var(--sp-4) 0;
  font-size: var(--fs-md);
  line-height: 1.45;
  color: ${(props) => props.theme.colors.text};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const AvisoLeituraNota = styled.p`
  margin: var(--sp-8) 0 0 0;
  padding-top: var(--sp-8);
  border-top: 1px solid
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  font-size: var(--fs-sm);
  line-height: 1.45;
  color: ${(props) => props.theme.colors.label};
`;

// ===== CARTÃO =====
export const Cartao = styled.div`
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  padding: var(--sp-20);
  margin-bottom: var(--sp-16);
  animation: ${fadeIn} 0.3s ease;
`;

export const CartaoCabecalho = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  flex-wrap: wrap;
  margin-bottom: var(--sp-8);
`;

export const CartaoTitulo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const CartaoTexto = styled.p`
  margin: 0 0 var(--sp-8) 0;
  font-size: var(--fs-md);
  line-height: 1.5;
  color: ${(props) => props.theme.colors.label};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CartaoNota = styled.div`
  margin-top: var(--sp-12);
  padding-top: var(--sp-10);
  border-top: 1px solid
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  font-size: var(--fs-sm);
  line-height: 1.45;
  color: ${(props) => props.theme.colors.label};
`;

export const SecaoTitulo = styled.h2`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin: var(--sp-24) 0 var(--sp-12) 0;
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

// ===== INDICADORES (contagens) =====
export const GradeIndicadores = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 190), 1fr));
  gap: var(--sp-12);
  margin-bottom: var(--sp-16);
`;

export const Indicador = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 8px;
  padding: var(--sp-16);
`;

export const IndicadorNumero = styled.div<{ $tom?: "neutro" | "alerta" }>`
  font-size: var(--fs-4xl);
  font-weight: 700;
  line-height: 1.1;
  color: ${(props) =>
    props.$tom === "alerta" ? "#be3028" : props.theme.colors.text};
`;

export const IndicadorRotulo = styled.div`
  margin-top: var(--sp-4);
  font-size: var(--fs-md);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const IndicadorNota = styled.div`
  margin-top: var(--sp-2);
  font-size: var(--fs-sm);
  line-height: 1.4;
  color: ${(props) => props.theme.colors.label};
`;

// ===== ETIQUETAS =====
export const Etiqueta = styled.span<{ $tom?: "alta" | "media" | "baixa" | "ok" }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-2) var(--sp-8);
  border-radius: 999px;
  font-size: var(--fs-xs);
  font-weight: 600;
  white-space: nowrap;
  color: ${(props) => props.theme.colors.text};
  border: 1px solid
    ${(props) =>
      props.$tom === "alta"
        ? "#be3028"
        : props.$tom === "media"
        ? "#8f5e07"
        : props.$tom === "ok"
        ? "#26792f"
        : props.theme.colors.selection};
  background-color: ${(props) =>
    props.$tom === "alta"
      ? "#be302818"
      : props.$tom === "media"
      ? "#8f5e0718"
      : props.$tom === "ok"
      ? "#26792f18"
      : props.theme.colors.selection + "20"};
`;

// ===== LISTA NOMINAL DE REGISTROS =====
export const ListaRegistros = styled.ul`
  list-style: none;
  margin: var(--sp-12) 0 0 0;
  padding: 0;
  border-top: 1px solid
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
`;

export const RegistroLinha = styled.li`
  display: flex;
  align-items: baseline;
  gap: var(--sp-8);
  flex-wrap: wrap;
  padding: var(--sp-6) 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.selection + "20"};
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
`;

export const RegistroCodigo = styled.span`
  min-width: calc(var(--u) * 64);
  font-family: "Courier New", monospace;
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
`;

export const RegistroNome = styled.span`
  font-weight: 600;
`;

export const RegistroDetalhe = styled.span`
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
`;

export const BotaoTexto = styled.button`
  margin-top: var(--sp-10);
  padding: var(--sp-6) var(--sp-12);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "80"};
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);

  &:hover {
    background-color: ${(props) => props.theme.colors.selection + "30"};
  }
`;

// ===== BUSCA =====
export const BarraBusca = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-10);
  flex-wrap: wrap;
  margin-bottom: var(--sp-12);
`;

export const CampoBusca = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  flex: 1;
  min-width: calc(var(--u) * 240);
  padding: var(--sp-8) var(--sp-12);
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
`;

export const EntradaBusca = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }
`;

export const Seletor = styled.select`
  padding: var(--sp-8) var(--sp-10);
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  cursor: pointer;
`;

export const ContadorResultado = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  margin-bottom: var(--sp-8);
`;

// ===== TABELA =====
export const TabelaEnvolucro = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

export const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${(props) => props.theme.colors.modalColor};
`;

export const Th = styled.th<{ $direita?: boolean }>`
  position: sticky;
  top: 0;
  text-align: ${(props) => (props.$direita ? "right" : "left")};
  padding: var(--sp-10) var(--sp-12);
  font-size: var(--fs-sm);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: calc(var(--u) * 0.5);
  color: ${(props) => props.theme.colors.text};
  background-color: ${(props) => props.theme.colors.background};
  border-bottom: 1px solid ${(props) => props.theme.colors.selection + "50"};
`;

export const Td = styled.td<{ $direita?: boolean; $mono?: boolean }>`
  text-align: ${(props) => (props.$direita ? "right" : "left")};
  padding: var(--sp-8) var(--sp-12);
  font-size: var(--fs-md);
  font-family: ${(props) => (props.$mono ? '"Courier New", monospace' : "inherit")};
  color: ${(props) => props.theme.colors.text};
  border-bottom: 1px solid ${(props) => props.theme.colors.selection + "20"};
  vertical-align: top;
`;

export const Tr = styled.tr`
  &:hover td {
    background-color: ${(props) => props.theme.colors.selection + "18"};
  }
`;

// ===== AVISO INLINE =====
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-top: var(--sp-12);
  padding: var(--sp-10) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.45;
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-16);
  padding: calc(var(--u) * 60);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: var(--sp-40);
  height: var(--sp-40);
  border: 4px solid ${(props) => props.theme.colors.selection + "30"};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  border: 2px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
